import WireframeLayout, { buildSidebarSections } from '@/components/WireframeLayout'
import { cn, Badge, Card, Progress, KPI } from '../components/primitives'
import { WAREHOUSES, CHANNEL_META } from '../components/data'

const statusBorder = { healthy: "#10b981", warning: "#f59e0b", critical: "#ef4444", overstock: "#8b5cf6" }

// ─── Channel pill ────────────────────────────────────────────────────────────
const ChannelPill = ({ name }: { name: string }) => {
  const m = CHANNEL_META[name] || CHANNEL_META.DTC
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border", m.bg, m.text, m.border)}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: m.dot, display: "inline-block", flexShrink: 0 }} />
      {name}
    </span>
  )
}

// ─── Channel split mini-bar ──────────────────────────────────────────────────
const ChannelBar = ({ split, colors }: { split: Record<string, number>, colors: Record<string, string> }) => {
  const entries = Object.entries(split)
  return (
    <div className="flex rounded-full overflow-hidden h-2 w-full">
      {entries.map(([ch, pct]) => (
        <div key={ch} title={`${ch}: ${pct}%`} style={{ width: `${pct}%`, background: colors[ch] || "#94a3b8" }} />
      ))}
    </div>
  )
}

// ─── Warehouse Card ──────────────────────────────────────────────────────────
const WarehouseCard = ({ wh }: { wh: typeof WAREHOUSES[0] }) => {
  const critCount = wh.skus.filter(s => s.status === "critical").length
  const overCount = wh.skus.filter(s => s.status === "overstock").length

  return (
    <a
      href={`/wireframes/inventory/warehouse/${wh.id}`}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <Card
        style={{
          borderLeft: `3px solid ${statusBorder[wh.status] || "#94a3b8"}`,
          cursor: "pointer",
          transition: "all 0.15s",
        }}
        className="hover:shadow-md"
      >
        <div className="p-5">
          {/* Header with inline KPIs */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-bold text-slate-900">{wh.name}</span>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-[10px] text-slate-400 leading-none mb-0.5">SKUs</div>
                <div className="text-sm font-bold text-slate-900 leading-none">{wh.skuCount}</div>
              </div>
              <div style={{ width: 1, height: 24, background: "#e2e8f0" }} />
              <div className="text-right">
                <div className="text-[10px] text-slate-400 leading-none mb-0.5">Units</div>
                <div className="text-sm font-bold text-slate-900 leading-none">{(wh.totalUnits / 1000).toFixed(1)}K</div>
              </div>
              <div style={{ width: 1, height: 24, background: "#e2e8f0" }} />
              <div className="text-right">
                <div className="text-[10px] text-slate-400 leading-none mb-0.5">COG</div>
                <div className="text-sm font-bold text-emerald-700 leading-none">${(wh.cogValue / 1000).toFixed(0)}K</div>
              </div>
            </div>
          </div>

          {/* Capacity */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs text-slate-400">Warehouse Capacity</span>
              <span className={cn("text-xs font-semibold", wh.capacity > 85 ? "text-red-600" : wh.capacity > 70 ? "text-amber-600" : "text-emerald-600")}>
                {wh.capacity}% used
              </span>
            </div>
            <Progress value={wh.capacity} color={wh.capacity > 85 ? "#ef4444" : wh.capacity > 70 ? "#f59e0b" : "#10b981"} />
          </div>

          {/* Channels */}
          <div className="mb-3">
            <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Distribution Channels</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {wh.channels.map(ch => <ChannelPill key={ch} name={ch} />)}
            </div>
            <ChannelBar split={wh.channelSplit} colors={wh.channelColors} />
            <div className="flex gap-3 mt-1.5 flex-wrap">
              {Object.entries(wh.channelSplit).map(([ch, pct]) => (
                <div key={ch} className="flex items-center gap-1">
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: wh.channelColors[ch], display: "inline-block" }} />
                  <span className="text-[10px] text-slate-500">{ch} {pct}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Issue flags */}
          {(critCount > 0 || overCount > 0) && (
            <div className="flex gap-2 flex-wrap pt-3 border-t border-slate-100">
              {critCount > 0 && <Badge variant="critical">⚠ {critCount} SKU{critCount > 1 ? "s" : ""} critical stock</Badge>}
              {overCount > 0 && <Badge variant="overstock">↑ {overCount} SKU{overCount > 1 ? "s" : ""} overstock</Badge>}
            </div>
          )}
        </div>
      </Card>
    </a>
  )
}

// ─── Inventory Overview ──────────────────────────────────────────────────────
function InventoryOverview() {
  const totalSKUs = WAREHOUSES.reduce((a, w) => a + w.skuCount, 0)
  const totalUnits = WAREHOUSES.reduce((a, w) => a + w.totalUnits, 0)
  const totalCOG = WAREHOUSES.reduce((a, w) => a + w.cogValue, 0)
  const criticalSKUs = WAREHOUSES.reduce((a, w) => a + w.skus.filter(s => s.status === "critical").length, 0)
  const overStockSKUs = WAREHOUSES.reduce((a, w) => a + w.skus.filter(s => s.status === "overstock").length, 0)

  return (
    <div className="flex flex-col gap-6">
      {/* Top KPIs */}
      <div className="flex gap-3 flex-wrap">
        <KPI label="Total SKUs" value={totalSKUs} sub={`Across ${WAREHOUSES.length} warehouses`} />
        <KPI label="Total Units" value={`${(totalUnits / 1000).toFixed(1)}K`} sub="All locations combined" />
        <KPI label="Cost of Goods" value={`$${(totalCOG / 1000).toFixed(0)}K`} delta="Tied-up inventory value" />
        {criticalSKUs > 0 && (
          <KPI label="Critical Stock" value={criticalSKUs} delta={`${criticalSKUs} SKU${criticalSKUs > 1 ? "s" : ""} below safety stock`} dir="down" />
        )}
        {overStockSKUs > 0 && (
          <KPI label="Over Stock" value={overStockSKUs} delta={`${overStockSKUs} SKU${overStockSKUs > 1 ? "s" : ""} overstock`} dir="down" />
        )}
      </div>

      {/* Warehouse cards */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-slate-900">
            Warehouses <span className="font-normal text-slate-400 ml-1">({WAREHOUSES.length})</span>
          </h3>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
          {WAREHOUSES.map(wh => (
            <WarehouseCard key={wh.id} wh={wh} />
          ))}
        </div>
      </div>
    </div>
  )
}

export default function WithData() {
  return (
    <WireframeLayout
      title="Inventory"
      subtitle="Warehouses, SKU catalog & cost of goods"
      titleIcon="📦"
      sidebarLinks={buildSidebarSections(null, "inventory-data")}
    >
      <InventoryOverview />
    </WireframeLayout>
  )
}
